/**
 * Shared date-only helpers (`YYYY-MM-DD` calendar strings).
 *
 * Not UTC instants. Do not derive calendar days from ISO timestamp slices.
 * Do not parse date-only input with `new Date('YYYY-MM-DD')` (UTC midnight shifts local day).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const DATE_ONLY_CAPTURE = /^(\d{4})-(\d{2})-(\d{2})$/;

function daysInMonthUtc(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

function isValidCivilYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  const dim = daysInMonthUtc(year, month - 1);
  return day >= 1 && day <= dim;
}

function formatUtcYmd(msUtc: number): string | null {
  const d = new Date(msUtc);
  if (isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function isDateOnly(value: unknown): value is string {
  return parseDateOnly(value) !== null;
}

export function parseDateOnly(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  const m = DATE_ONLY_CAPTURE.exec(s);
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!isValidCivilYmd(year, month, day)) return null;

  return `${m[1]}-${m[2]}-${m[3]}`;
}

export function coerceFilterDateValueToDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const only = parseDateOnly(trimmed);
    if (only !== null) {
      const [ys, ms, ds] = only.split('-');
      const y = Number(ys);
      const mo = Number(ms);
      const d = Number(ds);
      const local = new Date(y, mo - 1, d);
      return isNaN(local.getTime()) ? null : local;
    }
    const ms = Date.parse(trimmed);
    if (Number.isNaN(ms)) return null;
    const parsed = new Date(ms);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function assertDateOnly(input: unknown, context?: string): string {
  const parsed = parseDateOnly(input);
  if (parsed === null) {
    const suffix = context ? ` (${context})` : '';
    throw new Error(`[date-timezone] Invalid date-only string${suffix}`);
  }
  return parsed;
}

export function dateOnlyFromLocalDate(date: Date): string | null {
  return formatDateOnlyLocal(date);
}

export function formatDateOnlyLocal(date: Date): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (!isValidCivilYmd(year, month, day)) {
    return null;
  }

  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

export function formatDateOnlyUtc(date: Date): string | null {
  if (!(date instanceof Date)) return null;
  if (isNaN(date.getTime())) return null;

  const year = date.getUTCFullYear();
  const month = pad2(date.getUTCMonth() + 1);
  const day = pad2(date.getUTCDate());
  return `${year}-${month}-${day}`;
}

export function todayAsDateOnlyLocal(now: Date = new Date()): string {
  const formatted = formatDateOnlyLocal(now);
  if (!formatted) throw new Error('Invalid Date provided to todayAsDateOnlyLocal');
  return formatted;
}

export function compareDateOnly(a: string, b: string): number {
  const left = parseDateOnly(a);
  const right = parseDateOnly(b);
  if (left && right) {
    return left.localeCompare(right);
  }
  return a.localeCompare(b);
}

/**
 * Today's calendar date as `YYYY-MM-DD` in the given IANA timezone (DST-safe via `Intl`).
 */
export function todayAsDateOnly(timeZone: string): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

function ymdToUtcMs(ymd: string): number {
  const parsed = parseDateOnly(ymd);
  if (!parsed) return NaN;
  const [yStr, mStr, dStr] = parsed.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  return Date.UTC(y, m - 1, d);
}

function utcMsToYmd(ms: number): string {
  return formatUtcYmd(ms) ?? '';
}

/**
 * Shifts `YYYY-MM-DD` by `deltaDays` using UTC calendar arithmetic (DST-safe for the string itself).
 * Returns the input unchanged when it is not a valid date-only string.
 */
export function shiftDateOnly(calendarDay: string, deltaDays: number): string {
  const base = ymdToUtcMs(calendarDay);
  if (!Number.isFinite(base)) return calendarDay;
  return utcMsToYmd(base + deltaDays * DAY_MS);
}

function assertValidDate(date: Date, context: string): void {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error(`Invalid date input (${context})`);
  }
}

/**
 * Extract a calendar-day string (`YYYY-MM-DD`) from a Date or string input.
 *
 * - If the value is already a bare `YYYY-MM-DD` string, it is returned as-is (no `Date` construction).
 * - Otherwise, the input is interpreted as a datetime and its **UTC date parts** are used to derive
 *   the calendar day string.
 */
export function toCalendarDay(value: Date | string): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const only = parseDateOnly(trimmed);
    if (only !== null) {
      return only;
    }
    const parsed = new Date(trimmed);
    assertValidDate(parsed, 'toCalendarDay(string)');
    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const d = String(parsed.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  assertValidDate(value, 'toCalendarDay(Date)');
  const y = value.getUTCFullYear();
  const m = String(value.getUTCMonth() + 1).padStart(2, '0');
  const d = String(value.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Alias for {@link toCalendarDay} — calendar day from a UTC instant. */
export const dateOnlyFromUtcDate = toCalendarDay;

export function dateOnlyIsBefore(a: string, b: string): boolean {
  return compareDateOnly(a, b) < 0;
}

export function dateOnlyIsOnOrAfter(a: string, b: string): boolean {
  return compareDateOnly(a, b) >= 0;
}

function getCivilDateKeyInTimeZone(instant: Date, iana: string): string | null {
  if (Number.isNaN(instant.getTime())) {
    return null;
  }

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: iana,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(instant);
    const y = parts.find((p) => p.type === 'year')?.value;
    const mo = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    if (!y || !mo || !d) {
      return null;
    }
    return `${y}-${mo}-${d}`;
  } catch {
    return null;
  }
}

export function anchorInstantForCivilDayInTimeZone(dateOnly: string, iana: string): Date | null {
  const parsed = parseDateOnly(dateOnly);
  if (!parsed) {
    return null;
  }

  const [ys, ms, ds] = parsed.split('-');
  const y = Number(ys);
  const mo = Number(ms);
  const d = Number(ds);
  let anchorMs = Date.UTC(y, mo - 1, d, 12, 0, 0);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const key = getCivilDateKeyInTimeZone(new Date(anchorMs), iana);
    if (key === parsed) {
      return new Date(anchorMs);
    }
    if (key === null) {
      return null;
    }
    if (key < parsed) {
      anchorMs += 24 * 60 * 60 * 1000;
    } else {
      anchorMs -= 24 * 60 * 60 * 1000;
    }
  }

  return null;
}

export function addDaysToDateOnly(dateOnly: string, days: number): string | null {
  const parsed = parseDateOnly(dateOnly);
  if (parsed === null || !Number.isInteger(days)) {
    return null;
  }
  const [ys, ms, ds] = parsed.split('-');
  const y = Number(ys);
  const mo = Number(ms);
  const d = Number(ds);
  const msUtc = Date.UTC(y, mo - 1, d + days);
  return formatUtcYmd(msUtc);
}

export function addDaysToDateOnlyLocal(base: Date, days: number): string {
  const next = new Date(base);
  if (isNaN(next.getTime()))
    throw new Error('Invalid base Date provided to addDaysToDateOnlyLocal');

  next.setDate(next.getDate() + days);
  const formatted = formatDateOnlyLocal(next);
  if (!formatted) throw new Error('Failed to format added date in addDaysToDateOnlyLocal');
  return formatted;
}
